import React from "react";
import { Modal, Button, Spin, Table, Progress, Tag } from "antd";
import {
  DownloadOutlined,
  CloseOutlined,
  BulbOutlined,
} from "@ant-design/icons";
import { Line, Column, Heatmap as AntHeatmap } from "@ant-design/plots";
import { useTranslation } from "react-i18next";
import { useGate } from "../../Subscription/entitlements";
import type {
  GenerateReportResponse,
  ReportContent,
  SummaryCard,
  Gauge,
  ReportChart,
  ReportTable,
  Breakdown,
  FeedItem,
  InsightItem,
  RecommendedAction,
} from "../../../types/report";

// ── Helpers ───────────────────────────────────────────────────────────────────

function renderWithEmphasis(
  text: string,
  emphasis: string[],
): React.ReactNode {
  if (!emphasis?.length) return text;
  const escaped = emphasis.map((e) =>
    e.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
  );
  const regex = new RegExp(`(${escaped.join("|")})`, "g");
  const parts = text.split(regex);
  return parts.map((part, i) =>
    emphasis.includes(part) ? (
      <strong key={i} style={{ color: "#4A1C96" }}>
        {part}
      </strong>
    ) : (
      part
    ),
  );
}

function renderMarkdownBold(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part,
  );
}

// ── Report Header ─────────────────────────────────────────────────────────────

const ReportHeader: React.FC<{
  title: string;
  subtitle: string;
  filtersApplied?: Record<string, any>;
  onDownload: () => void;
  onClose: () => void;
}> = ({ title, subtitle, filtersApplied, onDownload, onClose }) => {
  const { t } = useTranslation("dashboardModals");
  const activeFilters = Object.entries(filtersApplied ?? {});
  return (
    <div
      style={{
        background: "linear-gradient(135deg, #4A1C96 0%, #7B4DBF 100%)",
        padding: "20px 24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        color: "#fff",
        margin: "-24px -24px 0 -24px",
        borderRadius: "8px 8px 0 0",
      }}
    >
      <div>
        <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 4 }}>
          {title}
        </div>
        <div style={{ fontSize: 13, opacity: 0.85 }}>{subtitle}</div>
        {activeFilters.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              marginTop: 8,
            }}
          >
            {activeFilters.map(([col, filter]) => (
              <Tag
                key={col}
                style={{
                  background: "rgba(255,255,255,0.18)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.35)",
                  borderRadius: 4,
                  fontSize: 11,
                }}
              >
                {col}:{" "}
                {Array.isArray(filter?.value)
                  ? filter.value.join(", ")
                  : String(filter?.value ?? filter)}
              </Tag>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 4, marginTop: -4 }}>
        <Button
          type="text"
          icon={<DownloadOutlined style={{ fontSize: 18 }} />}
          style={{ color: "#fff" }}
          title={t("generateReportPreview.downloadAsPdf")}
          onClick={onDownload}
        />
        <Button
          type="text"
          icon={<CloseOutlined style={{ fontSize: 18 }} />}
          onClick={onClose}
          style={{ color: "#fff" }}
        />
      </div>
    </div>
  );
};

// ── Block: Summary Cards ──────────────────────────────────────────────────────

const SummaryCardsBlock: React.FC<{ cards: SummaryCard[] }> = ({ cards }) => {
  if (!cards?.length) return null;
  return (
    <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
      {cards.map((card, i) => (
        <div
          key={i}
          style={{
            flex: "1 1 0",
            minWidth: 110,
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: "12px 14px",
          }}
        >
          <div style={{ color: "#888", fontSize: 12, marginBottom: 4 }}>
            {card.label}
          </div>
          <div style={{ fontWeight: 700, fontSize: 20, color: "#222" }}>
            {card.value}
          </div>
          {card.delta != null && (
            <div
              style={{
                fontSize: 12,
                color: card.direction === "down" ? "#ff4d4f" : "#52c41a",
                marginTop: 3,
              }}
            >
              {card.direction === "down" ? "↓" : "↑"} {Math.abs(card.delta)}%
            </div>
          )}
          {card.sublabel && (
            <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
              {card.sublabel}
            </div>
          )}
          {card.subvalue && (
            <div style={{ fontSize: 11, color: "#999" }}>{card.subvalue}</div>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Block: Gauges ─────────────────────────────────────────────────────────────

const GaugesBlock: React.FC<{ gauges: Gauge[] }> = ({ gauges }) => {
  if (!gauges?.length) return null;
  return (
    <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
      {gauges.map((gauge, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: 16,
            textAlign: "center",
          }}
        >
          <div style={{ color: "#888", fontSize: 12, marginBottom: 8 }}>
            {gauge.label}
          </div>
          <Progress
            type="dashboard"
            percent={Math.round(gauge.percent)}
            size={100}
            strokeColor="#4A1C96"
            format={() => (
              <span
                style={{ fontWeight: 700, fontSize: 17, color: "#4A1C96" }}
              >
                {gauge.value}
              </span>
            )}
          />
          <div style={{ fontSize: 11, color: "#999", marginTop: 6 }}>
            {gauge.current?.toLocaleString()} / {gauge.target?.toLocaleString()}
          </div>
          {gauge.status && (
            <div
              style={{
                fontSize: 12,
                color: "#A072EB",
                fontWeight: 600,
                marginTop: 4,
              }}
            >
              {gauge.status}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Block: Charts ─────────────────────────────────────────────────────────────

const LineChartViz: React.FC<{ title: string; data: any }> = ({
  title,
  data,
}) => {
  const { t } = useTranslation("dashboardModals");
  if (!data?.series?.length) return null;
  const chartData = [
    ...data.series.map((d: any) => ({
      period: d.period,
      value: d.value,
      type: t("generateReportPreview.actualValueSeries"),
    })),
    ...data.series
      .filter((d: any) => d.expected != null)
      .map((d: any) => ({
        period: d.period,
        value: d.expected,
        type: t("generateReportPreview.linearRegressionSeries"),
      })),
  ];
  return (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: 16,
        flex: 1,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13 }}>
        {title}
      </div>
      <Line
        data={chartData}
        xField="period"
        yField="value"
        colorField="type"
        height={180}
        style={{ stroke: "#7B4DBF" }}
        axis={{ x: { labelAutoRotate: false } }}
      />
    </div>
  );
};

const BarChartViz: React.FC<{ title: string; data: any }> = ({
  title,
  data,
}) => {
  const series = data?.bins
    ? data.bins.map((b: any) => ({ x: b.range, value: b.count }))
    : (data?.series ?? []).map((s: any) => ({
        x: s.label ?? s.period ?? "",
        value: s.value,
      }));
  if (!series.length) return null;
  return (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: 16,
        flex: 1,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13 }}>
        {title}
      </div>
      <Column
        data={series}
        xField="x"
        yField="value"
        height={180}
        style={{ fill: "#7B4DBF" }}
        axis={{ x: { labelAutoRotate: false } }}
      />
    </div>
  );
};

const DonutChartViz: React.FC<{ title: string; data: any }> = ({
  title,
  data,
}) => {
  const { t } = useTranslation("dashboardModals");
  if (!data?.series?.length) return null;
  const toneColor: Record<string, string> = {
    positive: "#52c41a",
    neutral: "#faad14",
    negative: "#ff4d4f",
  };
  const items: Array<{ label: string; pct: number; color: string }> =
    data.series.map((s: any) => ({
      label: s.label,
      pct: s.percent ?? Math.round((s.value / (data.total ?? 1)) * 100),
      color: toneColor[s.tone?.toLowerCase?.()] ?? "#A072EB",
    }));

  let accumulated = 0;
  const stops = items
    .map((seg) => {
      const start = accumulated;
      accumulated += seg.pct;
      return `${seg.color} ${start}% ${accumulated}%`;
    })
    .join(", ");

  return (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: 16,
        flex: 1,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 13 }}>
        {title}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
        <div
          style={{
            width: 110,
            height: 110,
            borderRadius: "50%",
            background: `conic-gradient(${stops})`,
            flexShrink: 0,
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 22,
              borderRadius: "50%",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
              color: "#444",
            }}
          >
            {items[0]?.pct}%
          </div>
        </div>
        <div>
          {items.map((item, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 6,
                fontSize: 12,
              }}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: item.color,
                  flexShrink: 0,
                }}
              />
              <span style={{ color: "#555" }}>{item.label}</span>
              <span style={{ fontWeight: 700, color: "#333" }}>
                {item.pct}%
              </span>
            </div>
          ))}
          {data.total && (
            <div style={{ color: "#999", fontSize: 11, marginTop: 4 }}>
              {t("generateReportPreview.total", {
                total: data.total.toLocaleString(),
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const HeatmapViz: React.FC<{ title: string; data: any }> = ({
  title,
  data,
}) => {
  if (!data?.matrix?.length) return null;
  const flat: Array<{ day: string; hour: string; value: number }> = [];
  data.matrix.forEach((row: any) => {
    (row.hours as number[]).forEach((val, h) => {
      flat.push({ day: row.day, hour: `${h}h`, value: val });
    });
  });
  return (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: 16,
        flex: 1,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13 }}>
        {title}
      </div>
      <AntHeatmap
        data={flat}
        xField="hour"
        yField="day"
        colorField="value"
        height={180}
        style={{ fill: "#7B4DBF" }}
      />
    </div>
  );
};

const WordCloudViz: React.FC<{ title: string; data: any }> = ({
  title,
  data,
}) => {
  if (!data?.words?.length) return null;
  const maxCount = Math.max(...data.words.map((w: any) => w.count));
  const palette = [
    "#4A1C96",
    "#A072EB",
    "#7B4DBF",
    "#52c41a",
    "#faad14",
    "#ff4d4f",
    "#1890ff",
  ];
  return (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: 16,
        flex: 1,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13 }}>
        {title}
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          alignItems: "center",
          minHeight: 130,
          justifyContent: "center",
        }}
      >
        {data.words.slice(0, 30).map((word: any, i: number) => (
          <span
            key={i}
            style={{
              fontSize: 11 + Math.round((word.count / maxCount) * 20),
              color: palette[i % palette.length],
              fontWeight: word.count > maxCount * 0.6 ? 700 : 400,
              lineHeight: 1.3,
            }}
          >
            {word.text}
          </span>
        ))}
      </div>
    </div>
  );
};

const MapViz: React.FC<{ title: string; data: any }> = ({ title, data }) => {
  if (!data?.regions?.length) return null;
  const bucketColor: Record<string, string> = {
    High: "#4A1C96",
    Medium: "#A072EB",
    Low: "#D9B8FF",
  };
  return (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: 16,
        flex: 1,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13 }}>
        {title}
      </div>
      <div style={{ maxHeight: 200, overflowY: "auto" }}>
        {data.regions.slice(0, 14).map((r: any, i: number) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 6,
              fontSize: 12,
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: bucketColor[r.bucket] ?? "#ccc",
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1, color: "#444" }}>{r.region}</div>
            <div style={{ color: "#666", fontWeight: 600 }}>
              {r.value?.toLocaleString?.() ?? r.value}
            </div>
            <Tag
              style={{
                fontSize: 10,
                color: bucketColor[r.bucket] ?? "#999",
                borderColor: (bucketColor[r.bucket] ?? "#ccc") + "50",
              }}
            >
              {r.bucket}
            </Tag>
          </div>
        ))}
      </div>
    </div>
  );
};

const ChartsBlock: React.FC<{ charts: ReportChart[] }> = ({ charts }) => {
  if (!charts?.length) return null;

  const renderChart = (chart: ReportChart, key: number) => {
    switch (chart.type) {
      case "line":
        return <LineChartViz key={key} title={chart.title} data={chart.data} />;
      case "bar":
      case "histogram":
        return <BarChartViz key={key} title={chart.title} data={chart.data} />;
      case "pie":
      case "donut":
        return (
          <DonutChartViz key={key} title={chart.title} data={chart.data} />
        );
      case "heatmap":
        return (
          <HeatmapViz key={key} title={chart.title} data={chart.data} />
        );
      case "wordcloud":
        return (
          <WordCloudViz key={key} title={chart.title} data={chart.data} />
        );
      case "map":
        return <MapViz key={key} title={chart.title} data={chart.data} />;
      default:
        return null;
    }
  };

  const rows: ReportChart[][] = [];
  for (let i = 0; i < charts.length; i += 2) {
    rows.push(charts.slice(i, i + 2));
  }

  return (
    <>
      {rows.map((row, ri) => (
        <div key={ri} style={{ display: "flex", gap: 12, marginTop: 12 }}>
          {row.map((chart, ci) => renderChart(chart, ri * 2 + ci))}
        </div>
      ))}
    </>
  );
};

// ── Block: Tables ─────────────────────────────────────────────────────────────

const TablesBlock: React.FC<{ tables: ReportTable[] }> = ({ tables }) => {
  if (!tables?.length) return null;
  return (
    <>
      {tables.map((table, i) => (
        <div key={i} style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
            {table.title}
          </div>
          <Table
            size="small"
            columns={table.columns.map((col) => ({
              title: col.label,
              dataIndex: col.key,
              key: col.key,
            }))}
            dataSource={table.rows.map((r, ri) => ({ ...r, key: ri }))}
            pagination={false}
            scroll={{ y: 220 }}
          />
        </div>
      ))}
    </>
  );
};

// ── Block: Breakdowns ─────────────────────────────────────────────────────────

const BreakdownsBlock: React.FC<{ breakdowns: Breakdown[] }> = ({
  breakdowns,
}) => {
  if (!breakdowns?.length) return null;
  return (
    <>
      {breakdowns.map((bd, i) => (
        <div
          key={i}
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: 16,
            marginTop: 12,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 13 }}>
            {bd.title}
          </div>
          {bd.items.map((item, j) => (
            <div key={j} style={{ marginBottom: 10 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                  marginBottom: 3,
                }}
              >
                <div>
                  <span style={{ color: "#444" }}>{item.label}</span>
                  {item.description && (
                    <span style={{ color: "#999", marginLeft: 6, fontSize: 11 }}>
                      {item.description}
                    </span>
                  )}
                </div>
                <span style={{ fontWeight: 700, color: "#4A1C96" }}>
                  {item.percent}%
                </span>
              </div>
              <div
                style={{ background: "#f0f0f0", borderRadius: 4, height: 6 }}
              >
                <div
                  style={{
                    width: `${item.percent}%`,
                    background: "#4A1C96",
                    borderRadius: 4,
                    height: "100%",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      ))}
    </>
  );
};

// ── Block: Feed ───────────────────────────────────────────────────────────────

const FeedBlock: React.FC<{ items: FeedItem[] }> = ({ items }) => {
  const { t } = useTranslation("dashboardModals");
  if (!items?.length) return null;
  return (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: 16,
        marginTop: 12,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 13 }}>
        {t("generateReportPreview.interactionFeed")}
      </div>
      <div style={{ maxHeight: 200, overflowY: "auto" }}>
        {items.map((item, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              gap: 8,
              marginBottom: 10,
              fontSize: 12,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#A072EB",
                marginTop: 4,
                flexShrink: 0,
              }}
            />
            <div>
              <div style={{ color: "#999", fontSize: 11 }}>{item.time}</div>
              <div style={{ color: "#444" }}>{item.text}</div>
              {item.context && (
                <div style={{ color: "#999", fontSize: 11 }}>{item.context}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Block: Insights (with emphasis bolding) ───────────────────────────────────

const INSIGHT_TITLE_KEYS: Record<string, string> = {
  executive_brief: "executive_brief",
  operational_risk: "operational_risk",
  market_sentiment: "market_sentiment",
  geographic_performance: "geographic_performance",
  periodic_growth_review: "periodic_growth_review",
  user_engagement: "user_engagement",
};

const InsightsBlock: React.FC<{
  items: InsightItem[];
  reportType: string;
}> = ({ items, reportType }) => {
  const { t } = useTranslation("dashboardModals");
  if (!items?.length) return null;
  const insightTitleKey = INSIGHT_TITLE_KEYS[reportType];
  const title = insightTitleKey
    ? t(`generateReportPreview.insightTitles.${insightTitleKey}`)
    : t("generateReportPreview.insightTitles.default");
  return (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: 16,
        marginTop: 12,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13 }}>
        {title}
      </div>
      {items.map((item, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 8,
            fontSize: 13,
          }}
        >
          <div
            style={{ color: "#A072EB", marginTop: 2, flexShrink: 0 }}
          >
            ●
          </div>
          <div style={{ color: "#444", lineHeight: 1.55 }}>
            {renderWithEmphasis(item.text, item.emphasis ?? [])}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Block: Narrative Summary ──────────────────────────────────────────────────

const NarrativeSummaryBlock: React.FC<{ paragraphs: string[] }> = ({
  paragraphs,
}) => {
  if (!paragraphs?.length) return null;
  return (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: 16,
        marginTop: 12,
      }}
    >
      {paragraphs.map((para, i) => (
        <p
          key={i}
          style={{
            color: "#444",
            fontSize: 13,
            lineHeight: 1.7,
            margin: i < paragraphs.length - 1 ? "0 0 10px 0" : 0,
          }}
        >
          {renderMarkdownBold(para)}
        </p>
      ))}
    </div>
  );
};

// ── Block: Recommended Actions (footer) ───────────────────────────────────────

const RecommendedActionsBlock: React.FC<{
  actions: RecommendedAction[];
}> = ({ actions }) => {
  const { t } = useTranslation("dashboardModals");
  if (!actions?.length) return null;
  return (
    <div
      style={{
        background: "#f5eeff",
        borderRadius: 8,
        padding: "16px 20px",
        marginTop: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <BulbOutlined style={{ color: "#A072EB", fontSize: 16 }} />
        <div>
          <div style={{ fontWeight: 700, color: "#4A1C96", fontSize: 14 }}>
            {t("generateReportPreview.recommendedActions")}
          </div>
          <div style={{ color: "#777", fontSize: 12 }}>
            {t("generateReportPreview.recommendedActionsSubtitle")}
          </div>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${Math.min(actions.length, 3)}, 1fr)`,
          gap: 12,
        }}
      >
        {actions.slice(0, 3).map((action, i) => (
          <div key={i}>
            <div
              style={{
                color: "#4A1C96",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {action.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Layout-driven report body ─────────────────────────────────────────────────

const ReportBody: React.FC<{ content: ReportContent }> = ({ content }) => {
  const { t } = useTranslation("dashboardModals");
  if (!content.metadata.has_data) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
        {t("generateReportPreview.noDataAvailable")}
      </div>
    );
  }

  return (
    <>
      {content.metadata.layout.map((key) => {
        switch (key) {
          case "summary_cards":
            return (
              <SummaryCardsBlock key={key} cards={content.summary_cards} />
            );
          case "gauges":
            return <GaugesBlock key={key} gauges={content.gauges} />;
          case "charts":
            return <ChartsBlock key={key} charts={content.charts} />;
          case "tables":
            return <TablesBlock key={key} tables={content.tables} />;
          case "breakdowns":
            return (
              <BreakdownsBlock key={key} breakdowns={content.breakdowns} />
            );
          case "feed":
            return <FeedBlock key={key} items={content.feed} />;
          case "insights":
            return (
              <InsightsBlock
                key={key}
                items={content.insights}
                reportType={content.metadata.report_type}
              />
            );
          case "narrative_summary":
            return (
              <NarrativeSummaryBlock
                key={key}
                paragraphs={content.narrative_summary}
              />
            );
          case "recommended_actions":
            return (
              <RecommendedActionsBlock
                key={key}
                actions={content.recommended_actions}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
};

// ── Main modal component ──────────────────────────────────────────────────────

type Props = {
  open: boolean;
  loading: boolean;
  data: GenerateReportResponse | null;
  onClose: () => void;
};

const GenerateReportPreview: React.FC<Props> = ({
  open,
  loading,
  data,
  onClose,
}) => {
  const { t } = useTranslation("dashboardModals");
  const gate = useGate();
  const handleDownload = () => {
    if (!gate.canExport) {
      gate.requirePaid(
        () => {},
        t("generateReportPreview.downloadingReportFeature"),
      );
      return;
    }
    const styleId = "rpt-print-css";
    let style = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.id = styleId;
      document.head.appendChild(style);
    }
    style.innerHTML = `
      @media print {
        body * { visibility: hidden !important; }
        #generate-report-printable,
        #generate-report-printable * { visibility: visible !important; }
        #generate-report-printable {
          position: fixed !important;
          top: 0 !important; left: 0 !important;
          width: 100% !important;
          background: white !important;
          padding: 24px !important;
          box-sizing: border-box !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `;
    window.print();
    setTimeout(() => style?.remove(), 1000);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      closeIcon={null}
      centered
      styles={{
        body: { padding: "24px", maxHeight: "84vh", overflowY: "auto" },
      }}
    >
      {loading && (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: "#666", fontSize: 14 }}>
            {t("generateReportPreview.generatingReport")}
          </div>
        </div>
      )}

      {!loading && data?.content && (
        <div id="generate-report-printable">
          <ReportHeader
            title={data.content.header.title}
            subtitle={data.content.header.subtitle}
            filtersApplied={data.content.metadata.filters_applied}
            onDownload={handleDownload}
            onClose={onClose}
          />
          <ReportBody content={data.content} />
        </div>
      )}
    </Modal>
  );
};

export default GenerateReportPreview;
